import React from 'react';
import { BROKER_PALETTE_SIZE } from '../theme';

/**
 * Brand-neutral broker tile. Prefers a caller-supplied logo URL; falls
 * back to a deterministic color-initial avatar so we don't have to depend
 * on a third-party CDN for a complete broker list.
 *
 * The initial-avatar palette is theme-aware: it reads from CSS
 * variables (`--pc-color-tile-N-bg` / `--pc-color-tile-N-fg`) emitted
 * by `theme.ts`, so dark-mode embedders see muted backs with light-text
 * contrast instead of glaring light pastels.
 */

interface BrokerTileProps {
  name: string;
  /**
   * Used by callers to track which depository the broker belongs to.
   * Not currently rendered (logos and the broker name carry the brand);
   * kept on the API for analytics and future "depository" badges.
   */
  depository: 'CDSL' | 'NSDL';
  logo?: string;
  selected?: boolean;
  onClick?: () => void;
}

function hash(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
  return Math.abs(h);
}

export const BrokerTile: React.FC<BrokerTileProps> = ({
  name,
  depository: _depository,
  logo,
  selected,
  onClick,
}) => {
  // Pick one of N CSS-variable slots based on the broker name. Slots
  // are 1-indexed in theme.ts to match human-readable docs.
  const slot = (hash(name) % BROKER_PALETTE_SIZE) + 1;
  const initial = name.trim().charAt(0).toUpperCase() || '?';

  const [hover, setHover] = React.useState(false);
  const [imgErrored, setImgErrored] = React.useState(false);
  const showInitial = !logo || imgErrored;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected ? true : undefined}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        fontFamily: 'inherit',
        fontSize: 13,
        textAlign: 'left',
        width: '100%',
        borderRadius: 'var(--pc-radius-md)',
        border: `1.5px solid ${
          selected
            ? 'var(--pc-color-primary)'
            : hover
            ? 'var(--pc-color-primary)'
            : 'var(--pc-color-border)'
        }`,
        backgroundColor: hover || selected
          ? 'var(--pc-color-primary-soft)'
          : 'var(--pc-color-surface)',
        color: 'var(--pc-color-text)',
        cursor: 'pointer',
        transition: 'all 0.15s',
      }}
    >
      {showInitial ? (
        <span
          style={{
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: 'var(--pc-radius-sm)',
            backgroundColor: `var(--pc-color-tile-${slot}-bg)`,
            color: `var(--pc-color-tile-${slot}-fg)`,
            fontWeight: 700,
            fontSize: 13,
          }}
        >
          {initial}
        </span>
      ) : (
        <img
          src={logo}
          alt=""
          onError={() => setImgErrored(true)}
          style={{ width: 28, height: 28, objectFit: 'contain', flexShrink: 0 }}
        />
      )}
      <span style={{ minWidth: 0, flex: 1 }}>
        <span
          style={{
            display: 'block',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {name}
        </span>
      </span>
    </button>
  );
};
